import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { lazy, StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { RouteError } from './components/RouteError'
import { routeTree } from './routeTree.gen'
import './styles.css'

const Devtools = lazy(() => import('./components/devtools'))

/**
 * A deploy replaced the chunks this tab was built against.
 *
 * Every route is a lazy import, and a redeploy gives every chunk a new hash. A tab that was
 * already open then asks for a file that no longer exists, the SPA fallback answers with
 * index.html, and the browser reports `'text/html' is not a valid JavaScript MIME type` — which
 * says nothing about what happened and sends you looking at the wrong thing. It is a certainty
 * during a demo, because a demo is exactly when someone is redeploying.
 *
 * Reload once and the tab is on the new build. The sessionStorage flag is what keeps that from
 * becoming a loop if the chunk is missing for some other reason: fail twice and the real error
 * is allowed through.
 */
const RELOAD_FLAG = 'headline:reloaded-for-stale-chunk'
window.addEventListener('vite:preloadError', (event) => {
  try {
    // Already reloaded once for this: let the real error through rather than loop.
    if (sessionStorage.getItem(RELOAD_FLAG) !== null) return
    sessionStorage.setItem(RELOAD_FLAG, '1')
  } catch {
    // Private browsing, or storage disabled. Surfacing the original error beats a reload loop.
    return
  }
  event.preventDefault()
  window.location.reload()
})

// A load that got this far is running the current build, so the next stale chunk gets its retry.
window.addEventListener('load', () => {
  try {
    sessionStorage.removeItem(RELOAD_FLAG)
  } catch {
    // Nothing to clear if storage is unavailable.
  }
})

/**
 * This object *is* the cache. It lives in the tab's JS heap — not localStorage, not the
 * HTTP cache — so it's per-tab and discarded on a hard refresh.
 *
 * The two knobs that govern memory, both set explicitly because they're easy to forget:
 * - `staleTime`: how long data is served without a background refetch.
 * - `gcTime`: how long an entry with no mounted observers survives before collection. This
 *   is what stops the cache growing without bound.
 *
 * Lower `gcTime` per-query for anything with a large payload (long date ranges, big hit
 * lists), and keep high-cardinality values out of query keys — a key containing a search
 * string mints a new entry per keystroke, each held for the full `gcTime`.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
    },
  },
})

const router = createRouter({
  routeTree,
  context: { queryClient },
  // Prefetch on hover/focus: by the time a click lands, the next route's data is usually
  // already cached, so navigation renders in one paint instead of flashing a skeleton.
  defaultPreload: 'intent',
  // 0 disables the *router's* own loader cache, leaving Query as the single cache. Without
  // this you get two caching layers with independent expiry, and stale data that neither
  // one appears responsible for.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  // A loader or render that throws shows a sentence and a way back, not the router's bare
  // "Something went wrong!" with the raw error in red monospace.
  defaultErrorComponent: RouteError,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element #root not found')

// No auth provider, and no publishable key to check for: AuthKit runs entirely server-side, so
// the browser holds an HttpOnly cookie it can't read and the SPA asks /api/auth/me who it is.
// That also means there's no auth SDK to boot before the app can render.
createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {import.meta.env.DEV && (
        <Suspense>
          <Devtools router={router} />
        </Suspense>
      )}
    </QueryClientProvider>
  </StrictMode>,
)
